export interface ErrorResult {
  errorCode?: string;
  allErrorCodes?: string[];
  errorMessage?: string;
}

export interface ErrorResponse {
  message: string;
  graphQLErrors: {
    message: string;
    extensions: ErrorExtension;
  }[];
}
interface ErrorDetail {
  errorCode: string;
  errorMessage: string;
}

interface ErrorExtension {
  errors: ErrorDetail[];
}

// Start Copilot generated
// Runtime type checking utilities to safely validate data types at runtime
// These protect against malformed GraphQL responses that could cause page crashes
const isString = (value: unknown): value is string => typeof value === 'string';
const isArray = (value: unknown): value is unknown[] => Array.isArray(value);
const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

// Safe property access utility that prevents errors when traversing potentially malformed objects
// Returns defaultValue if any part of the path is null/undefined or doesn't exist
const safeGet = <T>(obj: unknown, path: string[], defaultValue: T): T => {
  try {
    const result = path.reduce((current, key) => {
      if (!isObject(current) || !(key in current)) {
        throw new Error('Path not found');
      }
      return current[key];
    }, obj);
    return result as T;
  } catch {
    return defaultValue;
  }
};

// Safe function execution utility that catches any runtime errors and returns fallback value
// Prevents uncaught exceptions from crashing the application
const safeTry = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};
// End Copilot generated

const getIsAuthError = (error: ErrorResponse) => {
  const graphQLErrors = safeGet(error, ['graphQLErrors'], []);
  if (!isArray(graphQLErrors)) return false;

  return graphQLErrors.some((graphQLError) => {
    const message = safeGet(graphQLError, ['message'], '');
    return isString(message) && message.includes('AUTH_ERR_105');
  });
};

const hasErrorCode = (errorCode: string, error: ErrorResponse): boolean => {
  const graphQLErrors = safeGet(error, ['graphQLErrors'], []);
  if (!isArray(graphQLErrors)) return false;

  return graphQLErrors.some((graphQLError) => {
    const errors = safeGet(graphQLError, ['extensions', 'errors'], []);
    if (isArray(errors)) {
      const hasInExtensions = errors.some(
        (err) => isObject(err) && safeGet(err, ['errorCode'], '') === errorCode,
      );
      if (hasInExtensions) return true;
    }

    const message = safeGet(graphQLError, ['message'], '');
    return isString(message) && message.includes(errorCode);
  });
};

const extractErrorCode = (errorMessage: string): string | undefined => {
  if (!isString(errorMessage)) return undefined;

  return safeTry(() => {
    const errorPattern = /[A-Z]+_[A-Z]+_\d+/;
    const match = errorMessage.match(errorPattern);
    return match?.[0];
  }, undefined);
};

/**
 * Main function used by newrelic.ts for GraphQL error tracking
 * Safely extracts error information from potentially malformed GraphQL responses
 */
// Helper function to collect all error codes from GraphQL response
export const collectAllGQLErrorCodes = (response: ErrorResponse): string[] => {
  const allErrorCodes: string[] = [];

  const graphQLErrors = safeGet(response, ['graphQLErrors'], []);
  if (!isArray(graphQLErrors)) return allErrorCodes;

  graphQLErrors.forEach((graphQLError) => {
    // Collect error codes from extensions
    const errors = safeGet(graphQLError, ['extensions', 'errors'], []);
    if (isArray(errors)) {
      errors.forEach((err) => {
        const errorCode = safeGet(err, ['errorCode'], '');
        if (
          isString(errorCode) &&
          errorCode &&
          !allErrorCodes.includes(errorCode)
        ) {
          allErrorCodes.push(errorCode);
        }
      });
    }

    // Collect error codes from direct messages
    const message = safeGet(graphQLError, ['message'], '');
    if (isString(message)) {
      const extractedCode = extractErrorCode(message);
      if (extractedCode && !allErrorCodes.includes(extractedCode)) {
        allErrorCodes.push(extractedCode);
      }
    }
  });

  return allErrorCodes;
};

export const trackGraphQlError = (response: ErrorResponse): ErrorResult => {
  // Start Copilot generated
  // Wraps the entire error processing logic in a safe try-catch to prevent page crashes
  // from malformed GraphQL error responses - returns fallback error message if anything fails
  return safeTry(
    () => {
      // End Copilot generated

      // Collect all error codes first
      const allErrorCodes = collectAllGQLErrorCodes(response);

      if (getIsAuthError(response)) {
        return {
          errorCode: 'AUTH_ERR_105',
          allErrorCodes: allErrorCodes.length > 0 ? allErrorCodes : undefined,
        };
      }

      if (hasErrorCode('PB_ERR_004', response)) {
        return {
          errorCode: 'PB_ERR_004',
          allErrorCodes: allErrorCodes.length > 0 ? allErrorCodes : undefined,
        };
      }

      if (allErrorCodes.length === 0) {
        const message = safeGet(response, ['message'], '');
        return { errorMessage: message || 'No errorCode provided' };
      }

      const firstErrorCode = allErrorCodes[0];
      return {
        errorCode: firstErrorCode,
        allErrorCodes,
      };
    },
    {
      errorMessage: 'An error occurred while processing the error response',
      allErrorCodes: undefined,
    },
  );
};

const v1ErrorCodes = [
  'PB_ERR_001',
  'PB_ERR_002',
  'PB_ERR_003',
  'PB_ERR_004',
  'PB_ERR_005',
  'PB_ERR_006',
  'PB_ERR_007',
];

const v2ErrorCodes = [
  'PB_ERR_008',
  'PB_ERR_009',
  'PB_ERR_010',
  'PB_ERR_011',
  'PB_ERR_012',
  'PB_ERR_013',
  'PB_ERR_014',
];

export const checkV1Error = (errors: Array<string>) => {
  return errors.some((code) => v1ErrorCodes.includes(code));
};

export const checkV2Error = (errors: Array<string>) => {
  return errors.some((code) => v2ErrorCodes.includes(code));
};
